import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import { debounce } from 'lodash'
import { type Contact } from 'entities/Contacts/model/Contact'
import { type IParamsConversationsSearch } from 'entities/Conversation/api/types'
import { type Tag } from 'entities/Tags/model/Tag'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { type User } from 'entities/Users/model/User'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SearchDropdownStore, SearchTypesEnum } from 'widgets/SearchDropdown'

type IConversationSearchStoreConfig = {
  onHasFilters?: (value: boolean) => void
  onResetConversations?: () => void
  onLoadConversations?: () => void
  onSearchConversations?: () => void
  onChangeSelectedSearch?: (value: string) => void
  onChangeHasSearchParams?: (value: boolean) => void
}

export class ConversationSearchStore {
  private _nextPage = 1
  private _perPage = 25
  private _total = 0

  private _opened = false
  private _selectedTypes: SearchTypesEnum[] = []
  private _selectedSearch = ''
  private _newType: SearchTypesEnum | null = null
  private _isDirtyFilter = false
  private _debounceFetch
  private _config: IConversationSearchStoreConfig | null = null
  private _hide = false

  private _disposeHasSearchParams: IReactionDisposer | null = null
  private _disposeOpened: IReactionDisposer | null = null
  private _disposeCurrentInboxId: IReactionDisposer | null = null
  private _disposeCurrentType: IReactionDisposer | null = null
  private _disposeSelectedTypes: IReactionDisposer | null = null
  private _disposeCurrentConversationId: IReactionDisposer | null = null
  private _disposeHasFilters: IReactionDisposer | null = null
  private _disposeNewType: IReactionDisposer | null = null
  private _disposeIsDirtyFilter: IReactionDisposer | null = null
  private readonly _searchDropdownStore: SearchDropdownStore

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._searchDropdownStore = new SearchDropdownStore(
      {
        includeItemsType: [SearchTypesEnum.contacts, SearchTypesEnum.teams],
      },
      this._entities
    )
    this._debounceFetch = debounce(this.resetSearch, 500)
    this._searchDropdownStore.initOptions({
      selectType: this.selectType,
      resetSearch: this.resetSearch,
      clearFilters: this.clearFilters,
      handleSearch: this.handleSearch,
      handleSelectedSearch: this.handleSelectedSearch,
      selectedMode: true,
    })

    this.reactionHasSearchParams()
    this.reactionOpened()
    this.reactionCurrentInboxId()
    this.reactionCurrentConversationId()
    this.reactionHasFilters()
    this.reactionCurrentType()
    this.reactionSelectedTypes()
    this.reactionNewType()
    this.reactionIsDirtyFilter()
  }

  disposeReactions = () => {
    this._disposeHasSearchParams?.()
    this._disposeOpened?.()
    this._disposeCurrentInboxId?.()
    this._disposeCurrentType?.()
    this._disposeSelectedTypes?.()
    this._disposeCurrentConversationId?.()
    this._disposeHasFilters?.()
    this._disposeNewType?.()
    this._disposeIsDirtyFilter?.()
  }

  setConfig = (config?: IConversationSearchStoreConfig) => {
    this._config = {
      ...this._config,
      ...config,
    }
  }

  get isOpened() {
    return this._opened
  }

  get isHide() {
    return this._hide
  }

  setHide = (value: boolean) => {
    this._hide = value
  }

  get selectedSearch() {
    return this._selectedSearch
  }

  get isActive() {
    return this.isOpened || this.hasSearchParams
  }

  get hasSearchParams() {
    return this._searchDropdownStore.hasSearchParams
  }

  get searchParams() {
    const { searchParams } = this._searchDropdownStore
    const params: IParamsConversationsSearch = {
      teams: Array.isArray(searchParams.teams) ? searchParams.teams : [],
      contacts: Array.isArray(searchParams.contacts) ? searchParams.contacts : [],
      page: this._nextPage,
      per_page: this._perPage,
    }

    return params
  }

  get hasMore() {
    return this._total > (this._nextPage - 1) * this._perPage
  }

  get hasSelectedSearch() {
    return !!this._selectedSearch
  }

  handleSelectedSearch = (value: string) => {
    this._selectedSearch = value
    this.resetSearch()
    this._config?.onChangeSelectedSearch?.(value)
  }

  handleSearch = (value: string) => {
    this._searchDropdownStore.handleSearch(value)
    this._selectedSearch = ''
    this._config?.onChangeSelectedSearch?.('')
  }

  clear = (isResearch = true) => {
    this._searchDropdownStore.clearFilters()
    this._searchDropdownStore.handleSearch('')
    this.resetNextPage()
    this._config?.onResetConversations?.()

    if (isResearch) {
      this.resetSearch()
    }
  }

  clearFilters = (isResearch = true) => {
    this._searchDropdownStore.clearFilters()
    this.resetNextPage()
    this._config?.onResetConversations?.()

    if (isResearch) {
      this.resetSearch()
    }
  }

  reset = () => {
    this.clear()
    this._opened = false
  }

  onOpen = (value?: string) => {
    this._opened = true
    if (value) {
      this.handleSearch(value)
      this.searchDropdownStore.actionSearch({ value })
    }
  }

  onClose = () => {
    this._opened = false
  }

  onToggle = () => {
    if (this._opened) {
      this.onClose()
    } else {
      this.onOpen()
    }
  }

  resetNextPage = () => {
    this._nextPage = 1
    this._total = 0
  }

  reactionHasSearchParams = () => {
    this._disposeHasSearchParams?.()
    this._disposeHasSearchParams = reaction(
      () => this.hasSearchParams,
      (value) => {
        this._config?.onChangeHasSearchParams?.(value)
      }
    )
  }

  reactionOpened = () => {
    this._disposeOpened?.()
    this._disposeOpened = reaction(
      () => this._opened,
      () => {
        this._searchDropdownStore.setNewType(null)
      }
    )
  }

  reactionCurrentInboxId = () => {
    this._disposeCurrentInboxId?.()
    this._disposeCurrentInboxId = reaction(
      () => this._entities.inboxesStore.currentInboxId,
      async (newValue, oldValue) => {
        if (oldValue === null) return
        if (this._searchDropdownStore.filtersCount) {
          this.reset()
        }
      }
    )
  }

  reactionCurrentType = () => {
    this._disposeCurrentType?.()
    this._disposeCurrentType = reaction(
      () => this._searchDropdownStore.currentType,
      (value) => {
        const status =
          this._searchDropdownStore.hasFilters &&
          this._searchDropdownStore.isOpen &&
          value === SearchTypesEnum.main

        this.handleDirtyFilter(status)
      }
    )
  }

  reactionSelectedTypes = () => {
    this._disposeSelectedTypes?.()
    this._disposeSelectedTypes = reaction(
      () => this._searchDropdownStore.selectedTypes,
      (value) => {
        this._selectedTypes = value
      }
    )
  }

  reactionCurrentConversationId = () => {
    this._disposeCurrentConversationId?.()
    this._disposeCurrentConversationId = reaction(
      () => this._entities.conversationStore.isNewConversation,
      (value) => {
        if (value && this._searchDropdownStore.filtersCount) this.reset()
      }
    )
  }

  reactionHasFilters = () => {
    this._disposeHasFilters?.()
    this._disposeHasFilters = reaction(
      () => this._searchDropdownStore.hasFilters,
      (value) => {
        this._config?.onHasFilters?.(value)
      }
    )
  }

  reactionNewType = () => {
    this._disposeNewType?.()
    this._disposeNewType = reaction(
      () => this._searchDropdownStore.newType,
      (value) => {
        this._newType = value
      }
    )
  }

  reactionIsDirtyFilter = () => {
    this._disposeIsDirtyFilter?.()
    this._disposeIsDirtyFilter = reaction(
      () => this._isDirtyFilter,
      (value) => {
        this._searchDropdownStore.setIsDirtyFilter(value)
      }
    )
  }

  handleDirtyFilter = (value: boolean) => {
    this._isDirtyFilter = value
  }

  selectType = (type: SearchTypesEnum) => {
    this.resetNextPage()
    this._searchDropdownStore.selectTypeParent(type)
  }

  selectTypeItem = (item: Contact | Tag | Inbox | User) => {
    this._searchDropdownStore.toggleTypeItem(item)
    this.resetSearch()
  }

  resetSearch = () => {
    this.resetNextPage()

    if (this.hasSearchParams) {
      this._config?.onSearchConversations?.()
    } else {
      this._config?.onResetConversations?.()
      this._config?.onLoadConversations?.()
    }
  }

  handleTotal(total: number) {
    this._total = total
  }

  handleNextPage(nextPage: number) {
    this._nextPage = nextPage
  }

  resetType() {
    this.resetSearch()
  }

  get searchDropdownStore() {
    return this._searchDropdownStore
  }

  get opened() {
    return this._opened
  }

  get total() {
    return this._total
  }
}
